import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';
import { ResourceTable } from '@diadraw/ui';
import { useResourceQuery } from '../hooks/useResourceQuery';
import { useAppointmentsWeek } from '../hooks/useAppointmentsWeek';
import { useSplit } from './SplitView';
import { CalendarWeekGrid } from './calendar/CalendarWeekParts';
import { ListPage } from './dashboard/ListPage';
import { ViewToggle, type BrowseView } from './dashboard/ViewToggle';
import { CalendarNav } from './dashboard/CalendarNav';
import {
  AppointmentDetailPanel,
  AppointmentStatusBadge,
  type AppointmentRow,
} from './AppointmentDetailPanel';

// Time · Client · Status · Booked by — no doctor column, a specialist only ever
// sees their own bookings (scoped at the ORM layer).
const COLUMN_ORDER = ['startsAt', 'patientName', 'status', 'source'];

/** Specialist home — mirrors the superdoc oversight home (eyebrow + greeting
 *  header, width-capped column) and the Appointments page's list ↔ calendar
 *  toggle, but scoped to the specialist's OWN booked appointments. Reads are
 *  policy-scoped in `schema.zmodel`, so the same queries return only their rows
 *  with no client-side filtering. Clicking a row opens the detail in the split. */
export function SpecialistDashboard() {
  const { t, i18n } = useTranslation();
  const crud = useClientQueries(schema);
  const split = useSplit();

  const { data: me } = crud.$procs.me.useQuery();
  const name = (me as { name?: string | null } | undefined)?.name;

  const [view, setView] = useState<BrowseView>('list');

  const where = useMemo(() => ({ status: 'BOOKED' }) as Record<string, unknown>, []);

  const appts = useResourceQuery('Appointment', {
    where,
    include: { call: { include: { transcript: true } }, healee: true },
    initialSorting: [{ id: 'startsAt', desc: true }],
    pageSize: 50,
  });

  // Calendar view pulls a broader (unpaginated) window; only fetched when active.
  const calendarQ = crud.appointment.useFindMany(
    {
      where,
      orderBy: { startsAt: 'asc' },
      take: 500,
    },
    { enabled: view === 'calendar' },
  );
  const week = useAppointmentsWeek(
    (calendarQ.data as Parameters<typeof useAppointmentsWeek>[0] | undefined) ?? [],
  );

  const dtFmt = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const columnRenderers = {
    startsAt: (value: unknown) =>
      value == null ? '—' : dtFmt.format(new Date(value as string)),
    patientName: (value: unknown, row: Record<string, unknown>) => {
      const name = (value as string | null) || '—';
      const phone = row.patientPhone as string | null;
      return (
        <div className="min-w-0">
          <div className="truncate">{name}</div>
          {phone ? <div className="text-xs text-muted-foreground tabular-nums">{phone}</div> : null}
        </div>
      );
    },
    status: (value: unknown, row: Record<string, unknown>) => (
      <AppointmentStatusBadge status={String(value)} rescheduled={Boolean(row.rescheduled)} />
    ),
    // "Booked by" — the voice assistant or the admin web panel.
    source: (value: unknown) => (
      <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
        {t(value === 'admin' ? 'appointments.sourceWeb' : 'appointments.sourceVoice')}
      </span>
    ),
  };

  return (
    <ListPage
      eyebrow={t('home.eyebrow')}
      title={t('home.greeting', { name: name || t('home.there') })}
      subtitle={t('dashboard.appointments.subtitleMine')}
      actions={<ViewToggle view={view} onChange={setView} />}
    >
      {view === 'list' ? (
        <ResourceTable
          {...appts}
          columnOrder={COLUMN_ORDER}
          columnRenderers={columnRenderers}
          currentUser={(me as Record<string, unknown> | undefined) ?? null}
          onRowClick={(row) =>
            split.open({
              direction: 'horizontal',
              defaultSize: 42,
              content: (
                <AppointmentDetailPanel key={row.id as string} appointment={row as AppointmentRow} onCancelled={() => split.close()} />
              ),
            })
          }
        />
      ) : (
        <div className="card overflow-hidden">
          <CalendarNav
            onPrev={() => week.setWeekOffset((o) => o - 1)}
            onNext={() => week.setWeekOffset((o) => o + 1)}
            prevLabel={t('appointments.prevWeek')}
            nextLabel={t('appointments.nextWeek')}
          >
            <span className="text-sm font-medium text-foreground tabular-nums">
              {week.rangeFmt.format(week.days[0])} – {week.rangeFmt.format(week.days[6])}
            </span>
          </CalendarNav>
          <div className="overflow-x-auto">
            <CalendarWeekGrid cal={week} />
          </div>
        </div>
      )}
    </ListPage>
  );
}
